import React from "react";

import DeleteMeetingReportConfirmationModal from "@app/analytics/components/reports/DeleteMeetingReportConfirmationModal";
import RemoveMeetingReportConfirmationModal from "@app/analytics/components/reports/RemoveMeetingReportConfirmationModal";
import RenameReportModal from "@app/analytics/components/reports/RenameReportModal";
import { UpsellModal } from "@app/analytics/components/UpsellModal";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useUpdateMeetingName from "@app/analytics/hooks/meetings/useUpdateMeetingName";
import useDeleteReport from "@app/analytics/MeetingReport/hooks/useDeleteReport";
import type { UserMeeting } from "@app/common/types";

interface ReportRemoveAction {
  action: "remove";
  report: UserMeeting;
}

interface ReportDeleteAction {
  action: "delete";
  report: UserMeeting;
}

interface UpsellAction {
  action: "upsell";
}

type ReportDetails = UserMeeting | { id: string; title: string };
interface RenameAction {
  action: "rename";
  report: ReportDetails;
}

export type ReportAction =
  | ReportDeleteAction
  | ReportRemoveAction
  | UpsellAction
  | RenameAction;

const DeleteReportConfirmation = ({
  report,
  onClose,
}: {
  report: UserMeeting;
  onClose: () => void;
}) => {
  const { deleteReport } = useDeleteReport();

  const handleConfirmDeleteReport = () => {
    deleteReport.mutate(report.id, {
      onSettled: onClose,
    });
  };

  return (
    <DeleteMeetingReportConfirmationModal
      open
      onClose={onClose}
      onConfirm={handleConfirmDeleteReport}
      loading={deleteReport.isPending}
    />
  );
};

const RemoveReportConfirmation = ({
  report,
  onClose,
}: {
  report: UserMeeting;
  onClose: () => void;
}) => {
  const { removeReport } = useDeleteReport();

  const handleConfirmRemoveReport = () => {
    removeReport.mutate(report.id, {
      onSettled: onClose,
    });
  };

  return (
    <RemoveMeetingReportConfirmationModal
      open
      onClose={onClose}
      onConfirm={handleConfirmRemoveReport}
      loading={removeReport.isPending}
    />
  );
};

const UpgradeModal = ({ onClose }: { onClose: () => void }) => {
  const { currentPlan, isEligibleForLegacyPricing } = useBillingContext();

  return (
    <UpsellModal
      open
      onClose={onClose}
      useLegacyPrices={isEligibleForLegacyPricing}
      currentPlan={currentPlan.type}
    />
  );
};

const RenameReportConfirmation = ({
  report,
  onClose,
}: {
  report: ReportDetails;
  onClose: () => void;
}) => {
  const { updateMeetingName, loading } = useUpdateMeetingName(report, {
    onSettled: onClose,
  });

  const handleEditTitle = (title: string) => {
    updateMeetingName(title);
  };

  return (
    <RenameReportModal
      open
      reportTitle={report.title}
      onClose={onClose}
      onConfirm={handleEditTitle}
      loading={loading}
    />
  );
};

const MeetingReportRowActionModals = ({
  action,
  onClose,
}: {
  action: ReportAction | null;
  onClose: () => void;
}) => {
  if (!action) return null;

  if (action.action === "upsell") {
    return <UpgradeModal onClose={onClose} />;
  }

  if (action.action === "delete") {
    return (
      <DeleteReportConfirmation report={action.report} onClose={onClose} />
    );
  }

  if (action.action === "remove") {
    return (
      <RemoveReportConfirmation report={action.report} onClose={onClose} />
    );
  }

  if (action.action === "rename") {
    return (
      <RenameReportConfirmation report={action.report} onClose={onClose} />
    );
  }

  return null;
};

export default MeetingReportRowActionModals;
